import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { FieldProps } from '@rjsf/utils';
import { CodingAgentMcpServer, McpTransport } from 'api/model';
import { iconButtonPillStyle } from 'components/button/iconButtonPillStyle';
import { ServerCard } from './McpServersField.styles';

let idCounter = 0;
const nextId = (): string => `mcp-${idCounter++}`;

type HeaderDraft = { id: string; key: string; value: string };
type ServerDraft = {
  id: string;
  name: string;
  transport: McpTransport;
  url: string;
  headers: HeaderDraft[];
};

const TRANSPORT_OPTIONS: { value: McpTransport; label: string }[] = [
  { value: McpTransport.HTTP, label: 'HTTP' },
  { value: McpTransport.SSE, label: 'SSE' },
];

const toDrafts = (servers: CodingAgentMcpServer[]): ServerDraft[] =>
  servers.map((server) => ({
    id: nextId(),
    name: server.name ?? '',
    transport: server.transport ?? McpTransport.HTTP,
    url: server.url ?? '',
    headers: Object.entries(server.headers ?? {}).map(([key, value]) => ({ id: nextId(), key, value })),
  }));

const serialize = (drafts: ServerDraft[]): CodingAgentMcpServer[] =>
  drafts.map((draft) => ({
    name: draft.name,
    transport: draft.transport,
    url: draft.url,
    headers: draft.headers.reduce<Record<string, string>>((acc, header) => {
      const key = header.key.trim();
      if (key) {
        acc[key] = header.value;
      }
      return acc;
    }, {}),
  }));

const McpServersField = (props: FieldProps) => {
  const { disabled, readonly } = props;
  const isDisabled = disabled || readonly;
  const [drafts, setDrafts] = useState<ServerDraft[]>(() =>
    toDrafts((props.formData as CodingAgentMcpServer[] | undefined) ?? []),
  );
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const setData = (newDrafts: ServerDraft[]) => {
    setDrafts(newDrafts);
    props.onChange(serialize(newDrafts));
  };

  const addServer = () =>
    setData([...drafts, { id: nextId(), name: '', transport: McpTransport.HTTP, url: '', headers: [] }]);

  const removeServer = (serverId: string) => setData(drafts.filter((server) => server.id !== serverId));

  const updateServer = (serverId: string, patch: Partial<Omit<ServerDraft, 'id' | 'headers'>>) =>
    setData(drafts.map((server) => (server.id === serverId ? { ...server, ...patch } : server)));

  const addHeader = (serverId: string) =>
    setData(
      drafts.map((server) =>
        server.id === serverId
          ? { ...server, headers: [...server.headers, { id: nextId(), key: '', value: '' }] }
          : server,
      ),
    );

  const updateHeader = (serverId: string, headerId: string, patch: Partial<Omit<HeaderDraft, 'id'>>) =>
    setData(
      drafts.map((server) =>
        server.id === serverId
          ? {
              ...server,
              headers: server.headers.map((header) => (header.id === headerId ? { ...header, ...patch } : header)),
            }
          : server,
      ),
    );

  const removeHeader = (serverId: string, headerId: string) =>
    setData(
      drafts.map((server) =>
        server.id === serverId
          ? { ...server, headers: server.headers.filter((header) => header.id !== headerId) }
          : server,
      ),
    );

  const toggleReveal = (headerId: string) =>
    setRevealed((prev) => {
      const revealed = new Set(prev);
      if (revealed.has(headerId)) {
        revealed.delete(headerId);
      } else {
        revealed.add(headerId);
      }
      return revealed;
    });

  const nameCounts = drafts.reduce<Record<string, number>>((acc, server) => {
    const name = server.name.trim();
    if (name) {
      acc[name] = (acc[name] ?? 0) + 1;
    }
    return acc;
  }, {});

  const nameError = (name: string): string | undefined => {
    const trimmed = name.trim();
    if (!trimmed) {
      return 'Name is required';
    }
    if (nameCounts[trimmed] > 1) {
      return 'Server names must be unique';
    }
    return undefined;
  };

  return (
    <Box>
      <Typography variant="subtitle1">MCP servers</Typography>
      <Divider className="mb-3" />

      {drafts.length === 0 ? (
        <FormHelperText className="mb-3 ml-1">No MCP servers configured.</FormHelperText>
      ) : (
        <div className="space-y-3 mb-3">
          {drafts.map((server) => {
            const serverNameError = nameError(server.name);
            const urlError = server.url.trim() ? undefined : 'URL is required';

            return (
              <ServerCard key={server.id} variant="outlined" className="p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <TextField
                    label="Name"
                    required
                    value={server.name}
                    onChange={(e) => updateServer(server.id, { name: e.target.value })}
                    error={Boolean(serverNameError)}
                    helperText={serverNameError ?? 'Unique name of the server.'}
                    placeholder="my-server"
                    className="flex-1 min-w-40"
                    disabled={isDisabled}
                  />
                  <FormControl className="min-w-32" disabled={isDisabled}>
                    <InputLabel>Transport</InputLabel>
                    <Select
                      label="Transport"
                      value={server.transport}
                      onChange={(e) => updateServer(server.id, { transport: e.target.value as McpTransport })}
                    >
                      {TRANSPORT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Remove server">
                    <span>
                      <IconButton
                        aria-label="remove server"
                        className="mt-1"
                        onClick={() => removeServer(server.id)}
                        disabled={isDisabled}
                        sx={iconButtonPillStyle}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>

                <TextField
                  label="URL"
                  required
                  fullWidth
                  value={server.url}
                  onChange={(e) => updateServer(server.id, { url: e.target.value })}
                  error={Boolean(urlError)}
                  helperText={urlError}
                  placeholder="https://example.com/mcp"
                  disabled={isDisabled}
                />

                <div className="space-y-2">
                  <Typography variant="caption" color="text.secondary">
                    Headers
                  </Typography>
                  <Divider />
                  {server.headers.map((header) => {
                    const isRevealed = revealed.has(header.id);
                    return (
                      <div key={header.id} className="flex items-center gap-2">
                        <TextField
                          label="Key"
                          value={header.key}
                          onChange={(e) => updateHeader(server.id, header.id, { key: e.target.value })}
                          placeholder="Authorization"
                          className="flex-1"
                          disabled={isDisabled}
                        />
                        <TextField
                          label="Value"
                          type={isRevealed ? 'text' : 'password'}
                          value={header.value}
                          onChange={(e) => updateHeader(server.id, header.id, { value: e.target.value })}
                          className="flex-1"
                          disabled={isDisabled}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={isRevealed ? 'hide header value' : 'show header value'}
                                  size="small"
                                  edge="end"
                                  onClick={() => toggleReveal(header.id)}
                                  disabled={isDisabled}
                                >
                                  {isRevealed ? (
                                    <VisibilityOffIcon fontSize="small" />
                                  ) : (
                                    <VisibilityIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <IconButton
                          aria-label="remove header"
                          size="small"
                          onClick={() => removeHeader(server.id, header.id)}
                          disabled={isDisabled}
                          sx={iconButtonPillStyle}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    );
                  })}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addHeader(server.id)}
                    disabled={isDisabled}
                    sx={{ color: 'accent.main' }}
                  >
                    Header
                  </Button>
                </div>
              </ServerCard>
            );
          })}
        </div>
      )}

      <Button variant="contained" startIcon={<AddIcon />} onClick={addServer} disabled={isDisabled}>
        Add MCP server
      </Button>
    </Box>
  );
};

export default McpServersField;
